import React, {useMemo, useCallback} from 'react';
import styles from './DonationsTable.module.scss';
import {TableColumnConfig, Text, Spin, Button, Avatar} from '@gravity-ui/uikit';
import {DataTable, ProjectAvatar} from '../../shared';
import {DonationModel} from '../../entities';
import {useInfiniteScroll} from '../../shared/hooks/useInfiniteScroll';
import {useNavigate} from 'react-router';

interface DonationsTableProps {
  donations: DonationModel[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isFetchingNextPage: boolean;
  usersData?: Record<string, {displayName: string; avatarUrl: string | null}>;
}

export const DonationsTable = ({
  donations,
  isLoading,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
  usersData = {},
}: DonationsTableProps) => {
  const SKELETON_ROWS = 5;
  const navigate = useNavigate();

  const handleClickOnRow = useCallback(
    (donation: DonationModel) => {
      navigate(`/accounts/benefactors/profile/${donation.benefactorId}`);
    },
    [navigate],
  );

  const columns = useMemo<TableColumnConfig<DonationModel>[]>(
    () => [
      {
        id: 'displayName',
        name: 'Имя жертвователя',
        template: (donation: DonationModel) => {
          if (donation.type === 'Project') {
            return (
              <div className={styles.userCellContainer}>
                <ProjectAvatar size={28} />
                <Text variant="body-1">Проект НА СТАРТ</Text>
              </div>
            );
          }
          return (
            <div className={styles.userCellContainer}>
              <Avatar
                imgUrl={usersData[donation.benefactorId]?.avatarUrl || ''}
                text={
                  usersData[donation.benefactorId]?.displayName ? usersData[donation.benefactorId]?.displayName[0] : '?'
                }
                size="s"
              />
              <Text variant="body-1">{usersData[donation.benefactorId]?.displayName || 'Загрузка...'}</Text>
            </div>
          );
        },
      },
      {
        id: 'totalAmount',
        name: 'Сумма пожертвования',
        template: (donation: DonationModel) => <Text variant="body-1">{donation.totalAmount.toLocaleString()}</Text>,
      },
    ],
    [usersData],
  );

  const {loadMoreRef} = useInfiniteScroll({
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <div className={styles.tableContainer}>
      <DataTable<DonationModel>
        isLoading={isLoading && !isFetchingNextPage}
        data={donations || []}
        columns={columns}
        edgePadding={false}
        onRowClick={handleClickOnRow}
        skeletonRowCount={SKELETON_ROWS}
        emptyStateTitle="Жертвователи не найдены"
        emptyStateDescription="По данному сбору пока нет жертвователей"
      />

      {isFetchingNextPage ? (
        <div className={styles.loadingSpinnerContainer}>
          <Spin size="m" />
        </div>
      ) : (
        hasNextPage && (
          <Button
            component={'div'}
            size="xl"
            width="max"
            view="normal"
            ref={loadMoreRef}
            className={styles.loadMoreTrigger}>
            <Text variant="body-2">Загрузить ещё</Text>
          </Button>
        )
      )}
    </div>
  );
};
